<script lang="ts">
  import { fade } from 'svelte/transition';
  import { quadOut } from 'svelte/easing';
  import { onDestroy, onMount } from 'svelte';
  // RO Polyfill for older browsers
  //import ResizeObserver from 'resize-observer-polyfill';

  export let unique_id: string;
  export let item_height = 128;
  export let item_width = 0;
  export let viewport_height = 240;
  export let items: any[];
  export let i_selected = 0;
  export let multi = 1;
  export let auto_multi = false;
  export let onLimitReached;
  let item_width_orig = item_width;
  let viewport_height_orig = viewport_height;

  // NOTE: scroll_viewport may become null when component unloaded,
  // but functions inside setTimeout may run after unload anyway.
  let scroll_viewport: Element | null;
  let scroll_content: Element | null;

  //let n = 20; // Number of list elements that are recycled.
  let n = Math.floor(viewport_height/item_height*multi)+6; // Number of list elements that are recycled.
  let elements: HTMLDivElement[] = Array.from({length: n});
  let indices: number[] = Array.from({length: n}, (_, i)=>i);

  var i_first = 0;
  let rebuilding = false;
  let progress = 0.0;

  let rebuildIndices = (_, retry: boolean = true)=>{
    if (!scroll_viewport) return;

    let viewport = scroll_viewport.getBoundingClientRect();
    let rect = scroll_content.getBoundingClientRect();

    // If auto set, calculate number of columns that best fit the viewport width
    if (auto_multi) {
      multi = Math.max(1, Math.floor(rect.width / item_width_orig));
      item_width = rect.width / multi;
      viewport_height = Math.min(viewport_height_orig, 4+item_height*Math.ceil(items.length/multi));
    }

    // Recalculate number of visible items if viewport size changes.
    let n_new = Math.floor(viewport.height/item_height*multi)+6;
    if (n_new != n) {
      n = n_new;
      indices = Array.from({length: n}, (_, i)=>i);
      elements = Array.from({length: n});
    }

    let i_rebuild = Math.max(0, multi*Math.floor((viewport.top - rect.top)/item_height) - 3);

    if (retry) {
      window.setTimeout(()=>{
        rebuildIndices(0, false);
        rebuilding = false;
      }, 100)
    }
    if (i_first != i_rebuild || i_first + (n-1) > items.length) {
      i_first = i_rebuild;
      let i_last = i_first + n-1;
      indices.forEach((i, j_found) => {
        if (i == i_first) {
          indices = indices.map((_, j) => {
              let j_wrap = j-j_found;
              if (j_wrap < 0) j_wrap += n;
              return i + j_wrap;
          });
          return;
        } else if (i == i_last) {
          indices = indices.map((_, j) => {
              let j_wrap = j_found-j;
              if (j_wrap < 0) j_wrap += n;
              return i - j_wrap;
          });
          return;
        }
      });

      indices = indices.map((_, j) => {
          return i_rebuild+j;
      });
      //console.log(`rebuildingIndices ${indices[indices.length-1]} / ${items.length}`);
      if (onLimitReached && indices[indices.length-1] > (items.length-5)) {
        //console.log(`---> onLimitReached at ${indices[indices.length-1]} vs. ${items.length}`);
        onLimitReached();
      }
    }
  }
  let onScroll = ()=>{
    progress = scroll_viewport.scrollTop/(scroll_viewport.scrollHeight-scroll_viewport.clientHeight);
    if (rebuilding) return;
    rebuilding = true;
    window.requestAnimationFrame(rebuildIndices);
  }

  let resize_obs = new ResizeObserver(entries=> {
    onScroll();
  });
  onMount(()=>{
    resize_obs.observe(scroll_content);
  })
  onDestroy(()=>{
    resize_obs.unobserve(scroll_content);
    resize_obs.disconnect();
  })

  export function scrollTo(i) {
      // TODO: Any better way than setTimeout?
      // scrollTo doesn't work if called directly, maybe since DOM not
      // ready yet?
      window.setTimeout(()=>scroll_viewport?.scrollTo(0, (Math.floor(i/multi)-0.5)*item_height), 200);
  }

  function scrollToSelected(id, i) {
    if (unique_id != null && i !=-1 && !indices.includes(i)) {
      scrollTo(i);
    }
  }
  $: scrollToSelected(unique_id, i_selected);
</script>

<!-- Reset scroll so it doesn't persist next time page is loaded. -->
<svelte:window on:beforeunload={()=>scroll_viewport?.scrollTo(0,0)}/>

<flex-container>
  <slot name="header"/>
  {#if false}
  <progress-bar>
    <progress-cover style="width: {(1.0-progress)*100}%"/>
  </progress-bar>
  {/if}
    <item-list style="flex: 1 0 {viewport_height}px" on:scroll={onScroll} bind:this={scroll_viewport}>
      <item-spacer bind:this={scroll_content} style="height: {Math.ceil(items.length/multi)*item_height}px;">
        {#each indices as i, j (i)}
          <div class="fade-in" bind:this={elements[j]}  style="height: {item_height}px; width: {item_width == 0 ? "100%" : item_width+"px"}; top: {Math.floor(i/multi)*item_height}px; left: {(i%multi)*item_width}px; display: {i < items.length ? "block" : "none"}">
            {#if i < items.length}
            <slot name="item" item={items[i]} index={i}/>
            {/if}
          </div>
        {/each}
      </item-spacer>
    </item-list>
    <!--shadow/-->
</flex-container>

<style lang="scss">
  shadow {
    width:100%;
    height: 100%;
    box-shadow: inset 0px 0px 10px 5px #00000022;
    pointer-events: none;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }

  progress-bar {
    top: 0;
    //left: -15px;
    //width: calc(100% + 30px);
    width: 100%;
    height: 4px;
    background: linear-gradient(45deg, var(--color-xert-teal), darkturquoise);
    display: block;
    position: relative;
  }
  progress-cover {
    height: 100%;
    right: 0;
    background: lightgray;
    position: absolute;
    display: block;
  }

  flex-container {
    display: flex;
    flex-direction: column;
    align-content: stretch;
    height: 100%;
  }

  list-container {
    //width: calc(100% + 30px);
    //left: -15px;
    width: 100%;
    position: relative;
  }

  .fade-in {
    animation: fadeIn ease 0.2s;
  }
  @keyframes fadeIn {
    0% {opacity:0;}
    100% {opacity:1;}
  }


  item-list {
    width: 100%;
    //height: 100%;
    overflow-y: auto;
    //overflow-y: hidden;

    // Auto-hide scrollbar.
    // Also helps not capture scrolling when scrolling the outer view.
    //&:hover, &:active {
    //  overflow-y: scroll;
    //}
    overflow-x: hidden;
    background: white;
    display: flex;
    justify-content: center;
    item-spacer {
      background: white;
      position: relative;
      display: block;
      //width: 600px;
      //max-width: 99%;
      width: 100%;
      > div {
        position: absolute;
        width:100%;
        display: flex;
        justify-content: center;
      }
    }

  }
</style>